import { Article } from '../../components/Layout'
import { H1, H2, H3, P, Lead, UL, LI, Callout, CodeBlock, Code, Grid, Card } from '../../components/DocComponents'

const toc = [
  { id: 'overview',    title: 'Overview',              level: 2 as const },
  { id: 'endpoints',   title: 'Observability endpoints', level: 2 as const },
  { id: 'stack',       title: 'Local monitoring stack', level: 2 as const },
  { id: 'quickstart',  title: 'Quick-start',            level: 2 as const },
  { id: 'health',      title: 'Health endpoints',        level: 2 as const },
]

export function ObservabilityOverview() {
  return (
    <Article toc={toc}>
      <H1>Observability</H1>
      <Lead>
        Every Rabbit service exposes a Prometheus <Code>/metrics</Code> endpoint and emits
        OpenTelemetry traces. A one-command Docker stack brings up Jaeger, Prometheus, and
        Grafana pre-wired for all 8 services.
      </Lead>

      <H2 id="overview">Overview</H2>
      <P>
        Observability in Rabbit is built on three pillars:
      </P>
      <Grid cols={3}>
        <Card title="Metrics" icon="📊">
          Each service exposes Prometheus-format metrics at <Code>/metrics</Code>: HTTP latency
          histograms, request rate, error rate, and Node.js runtime metrics (heap, GC, event loop).
          Lens (Python) uses <Code>prometheus-fastapi-instrumentator</Code>.
        </Card>
        <Card title="Distributed Traces" icon="🔍">
          OpenTelemetry SDK with auto-instrumentation captures every HTTP request, database query,
          and outbound call as a span. Spans from all 8 services are linked by a shared
          <Code>traceparent</Code> header, forming end-to-end traces visible in Jaeger.
        </Card>
        <Card title="Structured Logs" icon="📋">
          All Winston loggers (Node.js) and the Python logger inject the active OTel
          <Code>traceId</Code> into every log line. This lets you jump from a log entry directly
          to its trace in Jaeger.
        </Card>
      </Grid>

      <H2 id="endpoints">Observability endpoints per service</H2>
      <CodeBlock language="text" title="Service endpoints">
{`Service          Port   Metrics endpoint   Health endpoint
─────────────────────────────────────────────────────────
auth-service     6000   /metrics           /api/health
brainer          3005   /metrics           /health
storage-service  7000   /metrics           /health
buzz             3000   /metrics           /health
mail-service     5000   /metrics           /health
raider-agent     3010   /metrics           /health
order-parser     3011   /metrics           /health
lens (Python)    3030   /metrics           /health`}
      </CodeBlock>
      <Callout type="info">
        All <Code>/metrics</Code> endpoints are unauthenticated — they are intended to be scraped
        by Prometheus running inside your private network. Do not expose them publicly.
      </Callout>

      <H2 id="stack">Local monitoring stack</H2>
      <P>
        A <Code>docker-compose.observability.yml</Code> at the repo root starts Jaeger,
        Prometheus, and Grafana with zero configuration:
      </P>
      <CodeBlock language="text" title="Monitoring stack URLs">
{`Jaeger UI      → http://localhost:16686
Prometheus UI  → http://localhost:9090
Grafana        → http://localhost:3002  (admin / rabbit)`}
      </CodeBlock>
      <P>
        Grafana is pre-provisioned with Prometheus and Jaeger as datasources plus a
        <strong> Rabbit — Service Overview</strong> dashboard showing HTTP latency p95/p99,
        request rate, error rate, heap usage, and event loop lag per service.
      </P>

      <H2 id="quickstart">Quick-start</H2>
      <CodeBlock language="bash" title="Start the monitoring stack">
{`# From the repo root — start Jaeger + Prometheus + Grafana
docker compose -f docker-compose.observability.yml up -d

# Verify all containers are running
docker compose -f docker-compose.observability.yml ps`}
      </CodeBlock>
      <CodeBlock language="bash" title="Configure services to send traces (add to each .env)">
{`OTEL_SERVICE_NAME=auth-service
OTEL_EXPORTER_OTLP_ENDPOINT=http://localhost:4318`}
      </CodeBlock>
      <P>
        Each service already defaults to <Code>http://localhost:4318</Code>, so traces are
        exported automatically when the Jaeger container is running — no <Code>.env</Code> change
        is required for local development.
      </P>
      <Callout type="tip">
        To shut down the monitoring stack without affecting running services:
        <Code>docker compose -f docker-compose.observability.yml down</Code>
      </Callout>

      <H2 id="health">Health endpoints</H2>
      <P>
        All health endpoints return a consistent JSON shape and a <Code>503</Code> status code
        when the database is unreachable — making them suitable as Kubernetes readiness probes.
      </P>
      <CodeBlock language="json" title="Healthy response (HTTP 200)">
{`{
  "status": "healthy",
  "service": "brainer",
  "uptime": 3842,
  "db": "ok",
  "timestamp": "2026-05-31T10:00:00.000Z"
}`}
      </CodeBlock>
      <CodeBlock language="json" title="Degraded response (HTTP 503)">
{`{
  "status": "degraded",
  "service": "brainer",
  "uptime": 3842,
  "db": "error",
  "timestamp": "2026-05-31T10:00:00.000Z"
}`}
      </CodeBlock>
    </Article>
  )
}
